import React, { useEffect, useRef, useState } from 'react'
import GradientCanvas from '../../components/GradientCanvas/GradientCanvas'
import SignatureButton from '../../components/SignatureButton/SignatureButton'
import { prefersLiteEffects } from '../../utils/perf'
import './Services.css'

const servicesList = [
  {
    num: '01',
    title: 'Frontend',
    tag: 'React · TypeScript',
    desc: 'Performance-minded interfaces built for clarity, accessibility, and scale.',
  },
  {
    num: '02',
    title: 'Backend',
    tag: 'Node · SQL · APIs',
    desc: 'Reliable services and data models that keep products moving under load.',
  },
  {
    num: '03',
    title: 'Design',
    tag: 'UI systems · UX',
    desc: 'Interface systems and flows that balance polish with practical usability.',
  },
  {
    num: '04',
    title: 'User Testing',
    tag: 'Usability · Research',
    desc: 'Feedback loops that turn real user friction into sharper releases.',
  },
  {
    num: '05',
    title: 'Automated Testing',
    tag: 'Unit · Integration · E2E',
    desc: 'Coverage that protects quality as velocity and complexity grow.',
  },
  {
    num: '06',
    title: 'Delivery',
    tag: 'Roadmaps · Execution',
    desc: 'From ambiguous goals to shipped outcomes, with the details handled.',
  },
]

const Services = () => {
  const heroRef = useRef(null)
  const rowsRef = useRef([])
  const [useLite] = useState(() => prefersLiteEffects())
  const [canvasVisible, setCanvasVisible] = useState(false)

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('visible')
            if (entry.target === heroRef.current) {
              setCanvasVisible(true)
            }
            observer.unobserve(entry.target)
          }
        })
      },
      { rootMargin: '0px 0px -80px 0px' }
    )

    if (heroRef.current) observer.observe(heroRef.current)
    rowsRef.current.forEach((row, index) => {
      if (row) {
        row.style.transitionDelay = `${index * 0.06}s`
        observer.observe(row)
      }
    })

    return () => observer.disconnect()
  }, [])

  return (
    <section id="services">
      <div className={`services-hero${useLite ? ' services-hero--static' : ''}`} ref={heroRef}>
        <div className="services-hero-canvas">
          {!useLite && canvasVisible ? (
            <GradientCanvas
              colors={['#06091b', '#16c79a', '#6b4bff', '#d24dff']}
              darkenTop
            />
          ) : null}
        </div>
        <div className="services-hero-grain" aria-hidden="true"></div>
        <div className="services-hero-scrim" aria-hidden="true"></div>
        <div className="services-hero-fade" aria-hidden="true"></div>

        <div className="services-hero-content">
          <span className="sectionEyebrow">What I do</span>
          <h2 className="services-heading">
            <span className="projectTextHeadingFancy">My</span> Specialties
          </h2>
          <p className="services-paragraph">
            I take products from early design through a live release, across the
            frontend, backend, and everything in between.
          </p>
        </div>
      </div>

      <div className="services-wrapper">
        <div className="services-list">
          {servicesList.map((item, index) => (
            <article
              key={item.title}
              className="services-row"
              ref={(el) => {
                rowsRef.current[index] = el
              }}
            >
              <span className="services-row-num">{item.num}</span>
              <div className="services-row-main">
                <h3 className="services-row-title">{item.title}</h3>
                <span className="services-row-tag">{item.tag}</span>
              </div>
              <p className="services-row-desc">{item.desc}</p>
            </article>
          ))}
        </div>

        <div className="services-cta">
          <p className="services-cta-text">
            Have something in mind? Let&apos;s scope it together.
          </p>
          <SignatureButton href="#contact">Start a project</SignatureButton>
        </div>
      </div>
    </section>
  )
}

export default Services
